import { ConflictRuleValidation, RuleDiagnostic } from "../models/ConflictRule";

export type RuleDiagnosticSeverity = "error" | "warning";

export interface PinnedRuleDiagnostic extends RuleDiagnostic {
  severity: RuleDiagnosticSeverity;
}

const CONDITION_ROW = /^\/(winner\/when|otherCopies\/onlyIf)\/(\d+)(\/.*)?$/;

/** The JSON pointer of one condition row, which shows the findings about any of its inputs. */
export const conditionRowPointer = (section: "/winner/when" | "/otherCopies/onlyIf", index: number): string =>
  `${section}/${index}`;

/**
 * The form field a finding is shown under. A finding about one item of a list the form edits as a whole — a family,
 * a case number, one half of the copy count — is shown under the list; a finding about one input of a condition is
 * shown under its row.
 */
const fieldOf = (pointer: string): string => {
  const row = CONDITION_ROW.exec(pointer);
  if (row) return `/${row[1]}/${row[2]}`;
  for (const list of ["/appliesTo/families", "/appliesTo/caseNumbers", "/checks/copyCount"])
    if (pointer === list || pointer.startsWith(`${list}/`)) return list;
  return pointer;
};

/**
 * The server's findings about a rule, sorted to the form fields that show them. Pointers come from the server's
 * validator, which pins each finding to the part of the rule it is about; anything the form has no field for (the
 * whole document, the schema version) is listed above the form instead, so no finding is ever hidden.
 */
export class RuleDiagnostics {
  private readonly byField = new Map<string, PinnedRuleDiagnostic[]>();
  private readonly errorPointers = new Set<string>();

  constructor(validation: ConflictRuleValidation | null | undefined) {
    const add = (diagnostic: RuleDiagnostic, severity: RuleDiagnosticSeverity) => {
      const field = fieldOf(diagnostic.pointer);
      this.byField.set(field, [...(this.byField.get(field) ?? []), { ...diagnostic, severity }]);
      if (severity === "error") this.errorPointers.add(diagnostic.pointer);
    };
    for (const error of validation?.errors ?? []) add(error, "error");
    for (const warning of validation?.warnings ?? []) add(warning, "warning");
  }

  /** The findings shown under one field. */
  at(field: string): PinnedRuleDiagnostic[] {
    return this.byField.get(field) ?? [];
  }

  /** Whether the input at exactly this pointer has an error, to mark it invalid. */
  hasErrorAt(pointer: string): boolean {
    return this.errorPointers.has(pointer);
  }

  /** The findings no rendered field shows. */
  outside(renderedFields: Iterable<string>): PinnedRuleDiagnostic[] {
    const rendered = new Set(renderedFields);
    return [...this.byField.entries()].filter(([field]) => !rendered.has(field)).flatMap(([, found]) => found);
  }
}
